import { useCallback, useMemo } from "react";

import { useMutation } from "@tanstack/react-query";

import Sentry from "@similarweb/sentry-logger";

import { isCustomerPaymentError } from "app/components/no-touch-purchase-modal/confirmation-modal/useNoTouchPurchaseQuery";
import { getSwitchPackageStageData } from "app/components/PackageSwitcher/components/SwitchPackageStage/stageConfig";
import { TrackingAction } from "app/components/PackageSwitcher/consts/TrackingActions";
import { openHooksModalV5 } from "app/services/ModalService";
import { allTrackers } from "app/services/track/track";

import { submitSwitchCurrentPackage } from "./packageSwitchApiService";
import type { IUseSubmitPackageChangeAPI, IUseSubmitPackageChangeProps } from "./useSubmitPackageChange.types";
import { SwitchPackageStageName } from "./useSubmitPackageChange.types";

interface ISwitchPackageMutationState {
    isPending: boolean;
    isSuccess: boolean;
    isError: boolean;
    error: unknown;
}

export const getSwitchPackageStageName = ({
    isPending,
    isSuccess,
    isError,
    error,
}: ISwitchPackageMutationState): SwitchPackageStageName | null => {
    if (isPending) {
        return SwitchPackageStageName.LOADING;
    }
    if (isSuccess) {
        return SwitchPackageStageName.SUCCESS;
    }
    if (isError) {
        return isCustomerPaymentError(error) ? SwitchPackageStageName.PAYMENT_FAILURE : SwitchPackageStageName.FAILURE;
    }
    return null;
};

export const useSubmitPackageChange = ({
    packageToChange,
    trackingInformation,
}: IUseSubmitPackageChangeProps): IUseSubmitPackageChangeAPI => {
    const handleSwitchPackageFailure = useCallback(() => {
        openHooksModalV5("ContactUs", undefined, "ContactUsPackageSwitcher", "GetLoading");
    }, []);

    const switchPackageMutation = useMutation({
        mutationFn: submitSwitchCurrentPackage,
        onError: (error) => {
            Sentry.errorTracker({
                e: error,
                message: "Error switching package",
                level: "error",
                params: {
                    extra: {
                        targetPackagePeriod: packageToChange?.period,
                        targetPackageTitle: packageToChange?.title,
                    },
                },
            });
        },
    });

    const switchPackageStageName = useMemo<SwitchPackageStageName | null>(
        () =>
            getSwitchPackageStageName({
                isPending: switchPackageMutation.isPending,
                isSuccess: switchPackageMutation.isSuccess,
                isError: switchPackageMutation.isError,
                error: switchPackageMutation.error,
            }),
        [
            switchPackageMutation.isPending,
            switchPackageMutation.isSuccess,
            switchPackageMutation.isError,
            switchPackageMutation.error,
        ],
    );

    const isInProcessOfPackageSwitching = switchPackageStageName !== null;
    const shouldCloseModalOnEsc =
        switchPackageStageName === SwitchPackageStageName.FAILURE ||
        switchPackageStageName === SwitchPackageStageName.PAYMENT_FAILURE;

    const switchPackageStageData = getSwitchPackageStageData(
        switchPackageStageName,
        handleSwitchPackageFailure,
        packageToChange?.title,
    );

    const onModalActionSuccess = useCallback(() => {
        window.location.reload();
    }, []);

    const handleSubmitButtonClick = useCallback(() => {
        if (!packageToChange) {
            return;
        }

        allTrackers.dispatchTrackEvent(
            "one click package",
            TrackingAction.CLICK,
            `${trackingInformation?.hookTrackingKey}+${packageToChange.package}/change plan`,
        );

        switchPackageMutation.mutate({
            targetPackagePeriod: packageToChange.period,
            targetPackageTitle: packageToChange.title,
        });
    }, [packageToChange, switchPackageMutation, trackingInformation]);

    return {
        switchPackageStageName,
        isInProcessOfPackageSwitching,
        switchPackageStageData,
        shouldCloseModalOnEsc,
        handleSubmitButtonClick,
        onModalActionSuccess,
    };
};
